import React from 'react'
import { Avatar, Box, Typography } from '@mui/material'
import MicIcon from '@mui/icons-material/Mic'
import MicOffIcon from '@mui/icons-material/MicOff'
import PersonIcon from '@mui/icons-material/Person'
import VideocamOffIcon from '@mui/icons-material/VideocamOff'

const mediaUrlPrefix = 'https://media.aumhum.com/images/prod/'

function parseRtmUsername(username) {
  if (!username || typeof username !== 'string' || !username.trim()) {
    return {
      role: null,
      name: '',
      profilePicId: null,
    }
  }

  try {
    const parsed = JSON.parse(username)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {
        role: null,
        name: username,
        profilePicId: null,
      }
    }

    return {
      role: parsed.role || null,
      name: parsed.name || '',
      profilePicId: parsed.profilePicId || null,
    }
  } catch (error) {
    return {
      role: null,
      name: username,
      profilePicId: null,
    }
  }
}

function getUsernameForUid(usernameMap, uid) {
  if (!usernameMap || uid === undefined || uid === null) {
    return ''
  }

  const uidNum = typeof uid === 'string' ? parseInt(uid, 10) : Number(uid)
  const uidStr = String(uid)

  return (
    usernameMap[uid] ||
    usernameMap[uidNum] ||
    usernameMap[uidStr] ||
    usernameMap[String(uidNum)] ||
    ''
  )
}

function getInitials(name) {
  if (!name || typeof name !== 'string') {
    return 'U'
  }

  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length >= 2) {
    return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase()
  }

  return words[0]?.[0]?.toUpperCase() || 'U'
}

function getProfilePictureUrl(profilePicId) {
  if (!profilePicId) {
    return null
  }

  if (profilePicId.startsWith('http://') || profilePicId.startsWith('https://')) {
    return profilePicId
  }

  return `${mediaUrlPrefix}${profilePicId}`
}

const ChamberVideoPlaceholder = ({
  user,
  uidToUsernameMap,
  chamberName,
  localRtmUsername = '',
}) => {
  const isLocalUser = user?.isLocal === true || String(user?.uid) === '0'
  const rawUsername =
    (isLocalUser ? localRtmUsername : '') || getUsernameForUid(uidToUsernameMap, user?.uid)
  const parsedUsername = parseRtmUsername(rawUsername)
  const displayName =
    (isLocalUser ? 'You' : '') ||
    parsedUsername.name ||
    (isLocalUser ? chamberName || 'Chamber Screen' : `Participant ${user?.uid ?? ''}`.trim())
  const hasAudio = user?.hasAudio === 1 || user?.hasAudio === true
  const profilePictureUrl = getProfilePictureUrl(parsedUsername.profilePicId)

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background:
          'radial-gradient(circle at top, rgba(233, 213, 255, 0.7) 0%, rgba(255, 255, 255, 0.98) 48%, rgba(243, 232, 255, 0.94) 100%)',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2.25,
          px: 3,
          py: 4,
          textAlign: 'center',
          background:
            'linear-gradient(180deg, rgba(255, 255, 255, 0.36) 0%, rgba(245, 243, 255, 0.54) 100%)',
        }}
      >
        <Box
          sx={{
            width: { xs: 92, md: 116 },
            height: { xs: 92, md: 116 },
            borderRadius: '30px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background:
              'linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(243, 232, 255, 0.98))',
            boxShadow:
              '0 24px 52px rgba(139, 92, 246, 0.14), inset 0 1px 0 rgba(255, 255, 255, 0.94)',
            border: '1px solid rgba(196, 181, 253, 0.86)',
            overflow: 'hidden',
          }}
        >
          {profilePictureUrl ? (
            <Avatar
              src={profilePictureUrl}
              alt={displayName}
              sx={{ width: '100%', height: '100%', borderRadius: '28px' }}
            />
          ) : (
            <Avatar
              sx={{
                width: '100%',
                height: '100%',
                borderRadius: '28px',
                background: 'transparent',
                color: '#6d28d9',
                fontSize: { xs: 30, md: 38 },
                fontWeight: 700,
              }}
            >
              {displayName ? getInitials(displayName) : <PersonIcon sx={{ fontSize: 36 }} />}
            </Avatar>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <VideocamOffIcon sx={{ color: '#7c3aed', fontSize: 20 }} />
          <Typography
            sx={{
              color: '#4c1d95',
              fontSize: { xs: 18, md: 22 },
              fontWeight: 700,
              letterSpacing: '-0.01em',
            }}
          >
            {displayName || 'Camera off'}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.5,
              py: 0.9,
              borderRadius: '999px',
              background: 'rgba(255, 241, 242, 0.94)',
              border: '1px solid rgba(251, 113, 133, 0.24)',
              color: '#be123c',
              boxShadow: '0 10px 26px rgba(139, 92, 246, 0.1)',
            }}
          >
            <VideocamOffIcon sx={{ fontSize: 16 }} />
            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
              Camera off
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.5,
              py: 0.9,
              borderRadius: '999px',
              background: hasAudio
                ? 'rgba(240, 253, 250, 0.94)'
                : 'rgba(255, 241, 242, 0.94)',
              border: hasAudio
                ? '1px solid rgba(45, 212, 191, 0.22)'
                : '1px solid rgba(251, 113, 133, 0.24)',
              color: hasAudio ? '#0f766e' : '#be123c',
              boxShadow: '0 10px 26px rgba(139, 92, 246, 0.1)',
            }}
          >
            {hasAudio ? (
              <MicIcon sx={{ fontSize: 16 }} />
            ) : (
              <MicOffIcon sx={{ fontSize: 16 }} />
            )}
            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
              {hasAudio ? 'Mic on' : 'Mic off'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default ChamberVideoPlaceholder
